import { loadSupersetFilters } from '../charts/superset-bundle.js';
import { createMonthFilterKeyboard } from '../filters/month-filter-keyboard.js';

/**
 * The Alpine half of <x-filters.month> and <x-filters.month-range>
 * (App\View\Components\Filters\MonthFilter): Superset's Month and Month Range filters from the
 * blink-superset bundle, bound to a Livewire property through x-modelable.
 *
 * mf_value IS the bound property: the token the picker writes (`month:2026-03`,
 * `monthrange:2026-01:2026-03`, a preset as `monthrange:preset:last_3_months`), or null. The host
 * sends the token only, never the DataMask's time_range, which the picker works out in the reader's
 * browser: the server resolves the token in the workspace's calendar.
 *
 * Three rules, each for a reason blink-superset-bundle.md ("The month hosts") gives:
 *   - PHP's months, always. A preset is mounted as the literal months the server rendered for it, so
 *     the picker never shows months the reader's browser worked out; the host tracks which preset
 *     that was, for the trigger's spoken value and the preset's pressed state.
 *   - A pick writes once. While the popup is open the host holds what the picker reports (a range's
 *     first click reports a one-month range) and writes the last value when it closes.
 *   - Superset's picker is not keyboard- or screen-reader-ready, so month-filter-keyboard.js repairs
 *     it from outside; the host tells it when the popup opens and closes.
 */

const MOUNTS = {
    month: ['mountMonthFilter', 'unmountMonthFilter'],
    monthRange: ['mountMonthRangeFilter', 'unmountMonthRangeFilter'],
};

const MONTH = '(\\d{4})-(0[1-9]|1[0-2])';
const LITERAL = {
    month: new RegExp(`^month:${MONTH}$`),
    monthRange: new RegExp(`^monthrange:${MONTH}:${MONTH}$`),
};
const PRESET_PREFIX = { month: 'month:preset:', monthRange: 'monthrange:preset:' };
const EMPTY_PRESETS = { order: [], literals: {}, labels: {} };

/**
 * The first and last month ('YYYY-MM') a literal token of this kind covers, or null for anything
 * else: what the adapter accepts, and what ReportFilterType's month types accept (year 0000 and a
 * range that ends before it starts are refused on both sides).
 */
export function monthsOf(kind, token) {
    const match = typeof token === 'string' ? LITERAL[kind]?.exec(token) : null;
    if (! match) return null;

    const first = `${match[1]}-${match[2]}`;
    const last = kind === 'month' ? first : `${match[3]}-${match[4]}`;

    return first.startsWith('0000') || last.startsWith('0000') || first > last ? null : [first, last];
}

/** What the picker is mounted with for a bound value: the literal token, and the preset it stands for. */
function toPicker(kind, value, presets) {
    if (value === null || value === undefined || value === '') return { token: null, presetKey: null, readable: true };

    if (typeof value === 'string' && value.startsWith(PRESET_PREFIX[kind])) {
        const key = value.slice(PRESET_PREFIX[kind].length);
        const literal = presets.literals?.[key];

        return monthsOf(kind, literal) ? { token: literal, presetKey: key, readable: true } : { token: null, presetKey: null, readable: false };
    }

    return monthsOf(kind, value) ? { token: value, presetKey: null, readable: true } : { token: null, presetKey: null, readable: false };
}

/** The chosen months in words, with a preset's name first: "Last 3 months: December 2025 to February 2026". */
function describe(kind, picker, presets, text) {
    const months = monthsOf(kind, picker.token);
    if (! months) return text.noValue;

    const name = (month) => `${text.months[Number(month.slice(5)) - 1]} ${month.slice(0, 4)}`;
    const span = months[0] === months[1] ? name(months[0]) : `${name(months[0])} ${text.through} ${name(months[1])}`;
    const preset = picker.presetKey ? presets.labels?.[picker.presetKey] : null;

    return preset ? `${preset}: ${span}` : span;
}

const isPresetToken = (kind, token) => typeof token === 'string' && token.startsWith(PRESET_PREFIX[kind]);

const sameToken = (a, b) => (a === '' || a === undefined ? null : a) === (b === '' || b === undefined ? null : b);

/**
 * The preset column and PHP's months, re-rendered by the server on every Livewire render. Read inside
 * mf_mount()'s try, so a script that does not parse shows the failed state and logs.
 */
function readPresets(root) {
    const node = root.querySelector('script[data-month-filter-presets]');
    return node ? { ...EMPTY_PRESETS, ...JSON.parse(node.textContent || '{}') } : EMPTY_PRESETS;
}

export default function MonthFilter(config = {}) {
    const kind = config.kind === 'monthRange' ? 'monthRange' : 'month';
    const [mountName, unmountName] = MOUNTS[kind];
    const text = config.text ?? {};

    // In the closure, not reactive: Alpine would deep-proxy the bundle and the React root it drives.
    let bundle = null;
    let keyboard = null;
    let observers = [];
    let alive = true;
    let mounted = false;
    // The value this host last wrote, until $watch hands it back.
    let sent;
    let hasSent = false;
    // The bound value the picker shows, so a $watch for it is not taken for a change.
    let mountedFor;
    // What the picker was last mounted with: its literal token and the preset it stands for.
    let shown = { token: null, presetKey: null };
    let isOpen = false;
    // What the picker reported while its popup was open; undefined when it reported nothing.
    let held;

    return {
        mf_value: null,
        mf_state: 'loading',
        mf_announcement: '',
        mf_valueText: '',
        // The literal token the picker shows, reactive so the Clear button follows it.
        mf_shownToken: null,
        mf_text: {
            months: Array.isArray(text.months) ? text.months : [],
            chosen: text.chosen ?? 'chosen',
            through: text.through ?? 'to',
            noValue: text.noValue ?? '',
            loadFailed: text.loadFailed ?? 'This month filter could not be loaded.',
            retry: text.retry ?? 'Retry',
        },

        init() {
            // Two hosts with one id share their label, hint and error ids, so each would name the
            // other's trigger; it happens whenever two components bind one name.
            const twins = document.querySelectorAll(`[data-month-filter="${config.id}"]`);
            if (twins.length > 1 && twins[0] !== this.$root) {
                console.error('Month filter id is not unique on the page: pass a distinct id', { id: config.id });
            }

            this.mf_valueText = this.mf_text.noValue;

            keyboard = createMonthFilterKeyboard({
                root: this.$root,
                mount: this.$refs.mount,
                popup: this.$refs.popup,
                ids: config.ids ?? {},
                text: this.mf_text,
                presets: () => readPresets(this.$root),
                // While the popup is open the grid shows what the reader has picked so far.
                chosenMonths: () => monthsOf(kind, held !== undefined ? held : shown.token),
                // A pick in progress is months the reader chose, so the preset it replaces is not pressed.
                currentPresetKey: () => (held !== undefined ? null : shown.presetKey),
            });

            this.$watch('mf_value', (value) => this.mf_follow(value));

            // Both are server-rendered on the morphable root, while the picker they describe is
            // React's: a validation error, and the preset months after a render.
            const invalid = new MutationObserver(() => this.mf_mount(this.mf_value));
            invalid.observe(this.$root, { attributes: true, attributeFilter: ['data-invalid'] });
            observers = [invalid];

            const presets = this.$root.querySelector('script[data-month-filter-presets]');
            if (presets) {
                const moved = new MutationObserver(() => this.mf_mount(this.mf_value));
                moved.observe(presets, { childList: true, characterData: true, subtree: true });
                observers.push(moved);
            }

            // A microtask later: x-modelable hands over the bound value in a microtask it queues
            // after init(), and a chunk already fetched would otherwise mount an empty picker first.
            queueMicrotask(() => this.mf_load());
        },

        destroy() {
            alive = false;
            observers.forEach((observer) => observer.disconnect());
            keyboard?.destroy();
            if (bundle && mounted) this.mf_unmount();
            bundle = null;
        },

        mf_load({ refocus = false } = {}) {
            if (! alive) return;
            this.mf_state = 'loading';

            loadSupersetFilters()
                .then((loaded) => {
                    if (! alive) return;
                    bundle = loaded;
                    this.mf_state = 'ready';
                    if (! this.mf_mount(this.mf_value)) return;
                    // A recovered filter must not leave "could not be loaded" behind in the alert.
                    this.mf_announcement = '';
                    if (refocus) keyboard.focusTrigger();
                })
                .catch((error) => this.mf_fail(error, refocus));
        },

        mf_retry() {
            this.mf_load({ refocus: true });
        },

        mf_fail(error, refocus = false) {
            console.error('Month filter failed to load', { id: config.id, error });
            bundle = null;
            mounted = false;
            this.mf_state = 'failed';
            // Cleared first and set on the next frame, so a second failure in a row is spoken again.
            this.mf_announcement = '';
            requestAnimationFrame(() => { this.mf_announcement = this.mf_text.loadFailed; });
            // Pressing Retry hid the button; a failure brings it back, and focus with it.
            if (refocus) this.$nextTick(() => this.$refs.retry?.focus());
        },

        mf_unmount() {
            bundle?.[unmountName]?.(this.$refs.mount);
            mounted = false;
        },

        /**
         * Mounts, or re-renders in place, for a bound value. Returns whether the picker is up. `fresh`
         * unmounts first: see mf_commit().
         */
        mf_mount(value, { fresh = false } = {}) {
            if (! bundle) return false;
            if (fresh && mounted) this.mf_unmount();

            let picker;
            const invalid = this.$root.dataset.invalid === 'true';

            try {
                const presets = readPresets(this.$root);
                picker = toPicker(kind, value, presets);
                if (! picker.readable) console.error('Month filter value is not one it can show', { id: config.id, value });

                bundle[mountName](this.$refs.mount, {
                    id: config.id,
                    filterState: { value: picker.token, validateStatus: invalid ? 'error' : undefined },
                    onDataMask: (dataMask) => this.mf_receive(dataMask),
                    onOpenChange: (open) => this.mf_openChanged(open),
                    getPopupContainer: () => this.$refs.popup,
                });

                this.mf_valueText = describe(kind, picker, presets, this.mf_text);
            } catch (error) {
                // The adapter registers its instance before it renders; left behind, Retry would
                // re-render into what the failed render left instead of mounting afresh.
                this.mf_unmount();
                this.mf_fail(error);
                return false;
            }

            mounted = true;
            mountedFor = value;
            shown = picker;
            this.mf_shownToken = picker.token;
            // The host's contrast repaints key off these (month-filter.blade.php).
            this.$refs.mount.dataset.invalid = invalid ? 'true' : 'false';
            this.$refs.mount.dataset.empty = picker.token === null ? 'true' : 'false';
            keyboard.wire({ invalid });

            return true;
        },

        mf_receive(dataMask) {
            const value = dataMask?.filterState?.value;
            const token = typeof value === 'string' && value !== '' ? value : null;

            if (isOpen) {
                held = token;
                return;
            }

            // Not inside React's event: a render there would batch into the event's own.
            queueMicrotask(() => this.mf_commit(token));
        },

        mf_openChanged(open) {
            isOpen = open;

            if (open) {
                keyboard.opened();
                return;
            }

            const token = held;
            held = undefined;

            queueMicrotask(() => {
                if (! alive || ! bundle) return;
                if (token !== undefined) this.mf_commit(token);
                keyboard.closed();
            });
        },

        /**
         * Writes the reader's value and mounts it back. A preset is mounted fresh with PHP's months:
         * the picker has taken the browser's months for it (handlePresetSelect), and follows only a
         * value that changed, so an in-place mount of the months already there (the preset re-picked,
         * or a preset whose months equal what was showing) would leave the browser's on the trigger.
         */
        mf_commit(token) {
            if (! alive || ! bundle) return;

            if (! sameToken(token, this.mf_value)) {
                sent = token;
                hasSent = true;
                this.mf_value = token;
            }

            this.mf_mount(token, { fresh: isPresetToken(kind, token) });
        },

        /** The host's own Clear button: Superset's sits inside the trigger, out of a screen reader's reach. */
        mf_clear() {
            // A range half-picked before the clear must not be written back when the popup closes.
            held = undefined;
            this.mf_commit(null);
            keyboard.focusTrigger();
        },

        mf_hasValue() {
            return this.mf_shownToken !== null;
        },

        mf_follow(value) {
            const own = hasSent && sameToken(value, sent);
            hasSent = false;
            sent = undefined;
            if (own || ! bundle || (mounted && sameToken(value, mountedFor))) return;

            this.mf_mount(value);
        },
    };
}
